import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, errorMessage, type User, type UserSettings, type Verification } from "./api";
import { noteAvatar } from "./avatars";

// ---------------------------------------------------------------- routing

export function navigate(to: string, replace = false) {
  if (to === location.pathname + location.search) return;
  if (replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  window.dispatchEvent(new Event("benleaf:navigate"));
}

export function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const update = () => setPath(location.pathname);
    window.addEventListener("popstate", update);
    window.addEventListener("benleaf:navigate", update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("benleaf:navigate", update);
    };
  }, []);
  return path;
}

export function Link({ to, children, className, title }: { to: string; children: ReactNode; className?: string; title?: string }) {
  return (
    <a
      href={to}
      className={className}
      title={title}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

// ---------------------------------------------------------------- session

interface SessionValue {
  user: User | null;
  loading: boolean;
  canManageSync: boolean;
  /** Only the server owner can connect Claude Code to a project. */
  canConnectClaude: boolean;
  /** Only the server owner can start a rave (Admin → Overview). */
  canRave: boolean;
  verification: Verification | null;
  setUser: (u: User | null) => void;
  refresh: () => Promise<void>;
  updateSettings: (patch: Partial<UserSettings>) => Promise<void>;
  logout: () => Promise<void>;
  theme: ThemePref;
  setTheme: (t: ThemePref) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [canManageSync, setCanManageSync] = useState(false);
  const [canConnectClaude, setCanConnectClaude] = useState(false);
  const [canRave, setCanRave] = useState(false);
  const [verification, setVerification] = useState<Verification | null>(null);
  const [localTheme, setLocalTheme] = useState(storedTheme);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: User; canManageSync: boolean; canConnectClaude: boolean; canRave: boolean; verification: Verification | null }>(
        "/api/auth/me",
      );
      setUser(res.user);
      setCanManageSync(res.canManageSync);
      setCanConnectClaude(!!res.canConnectClaude);
      setCanRave(!!res.canRave);
      setVerification(res.verification);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // So a new picture of your own shows everywhere at once.
  useEffect(() => {
    if (user) noteAvatar(user.id, user.avatar);
  }, [user]);

  useEffect(() => {
    refresh();
    const onUnauthorized = () => setUser(null);
    window.addEventListener("benleaf:unauthorized", onUnauthorized);
    return () => window.removeEventListener("benleaf:unauthorized", onUnauthorized);
  }, [refresh]);

  const updateSettings = useCallback(
    async (patch: Partial<UserSettings>) => {
      setUser((u) => (u ? { ...u, settings: { ...u.settings, ...patch } } : u));
      const res = await api.patch<{ user: User }>("/api/me", { settings: patch });
      setUser(res.user);
    },
    [],
  );

  const logout = useCallback(async () => {
    await api.post("/api/auth/logout").catch(() => {});
    setUser(null);
    navigate("/login");
  }, []);

  // The signed-in user's setting wins; otherwise this browser's last choice.
  const theme = user?.settings.theme ?? localTheme;
  const setTheme = useCallback(
    (t: ThemePref) => {
      setLocalTheme(t);
      if (user) updateSettings({ theme: t }).catch((e) => toast(errorMessage(e), "error"));
    },
    [user, updateSettings],
  );
  useEffect(() => {
    // Remembered per browser so the login page and the first paint (public/theme.js) match.
    setLocalTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {}
  }, [theme]);

  const value = useMemo(
    () => ({ user, loading, canManageSync, canConnectClaude, canRave, verification, setUser, refresh, updateSettings, logout, theme, setTheme }),
    [user, loading, canManageSync, canConnectClaude, canRave, verification, refresh, updateSettings, logout, theme, setTheme],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}

// ---------------------------------------------------------------- theme

export type ThemePref = NonNullable<UserSettings["theme"]>;

const THEME_KEY = "benleaf:theme";

function storedTheme(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {}
  return "system";
}

export function useAppliedTheme(pref: ThemePref) {
  const [systemDark, setSystemDark] = useState(() => matchMedia("(prefers-color-scheme: dark)").matches);
  useEffect(() => {
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const fn = () => setSystemDark(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  const theme = pref === "light" || pref === "dark" ? pref : systemDark ? "dark" : "light";
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  return theme;
}

// ---------------------------------------------------------------- toasts

export interface Toast {
  id: number;
  kind: "info" | "error" | "success";
  text: string;
}

let toastId = 0;
export function toast(text: string, kind: Toast["kind"] = "info") {
  window.dispatchEvent(new CustomEvent("benleaf:toast", { detail: { id: ++toastId, kind, text } }));
}

export function Toasts() {
  const [items, setItems] = useState<Toast[]>([]);
  useEffect(() => {
    const fn = (e: Event) => {
      const t = (e as CustomEvent<Toast>).detail;
      setItems((xs) => [...xs.slice(-3), t]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), t.kind === "error" ? 7000 : 3500);
    };
    window.addEventListener("benleaf:toast", fn);
    return () => window.removeEventListener("benleaf:toast", fn);
  }, []);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast toast-${t.kind}`} onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- misc

/** Stable colour for a user, used for cursors, avatars and tracked changes. */
export function userColor(id: number | string): string {
  const palette = ["#c0392b", "#2e7d5b", "#2f6fb0", "#a65d1e", "#7b4fa6", "#0f7f86", "#b03a6e", "#6b7d1d", "#976e09", "#4b5fbf"];
  const n = typeof id === "number" ? id : [...id].reduce((a, c) => a * 31 + c.charCodeAt(0), 7);
  return palette[Math.abs(n) % palette.length];
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function timeAgo(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  return new Date(ts).toLocaleDateString();
}

/** "main.tex", "main.tex and refs.bib", "main.tex and 3 more". */
export function fileList(files: string[]): string {
  if (files.length <= 2) return files.join(" and ");
  return `${files[0]} and ${files.length - 1} more`;
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function useLocalStorage<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    [key],
  );
  return [value, set];
}
